import React, { useState } from 'react';

import { Autocomplete, TextField, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import { capitalCase } from 'change-case';
import clsx from 'clsx';
import { FilterTags } from 'src/components/generic/data-grid/FilterTags';
import { DocumentStatusSimple } from 'src/components/reports';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
} from './autocomplete-filter-utils';
import { AlineCheckbox } from '../AlineCheckbox';

const useStyles = makeStyles()(theme => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
  },
  tag: {
    marginLeft: theme.spacing(1),
  },
}));

export function DocumentStatusFilterAutoComplete({ applyValue, item }: GridFilterInputValueProps) {
  const theme = useTheme();
  const { classes } = useStyles();
  const [truncateTags, setTruncateTags] = useState(true);
  const [values, setValues] = useState<Apollo.DocumentStatus[]>(item.value ?? []);
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: Apollo.DocumentStatus[]): void {
    applyValue({
      ...item,
      value,
    });
    setValues(value);
  }

  return (
    <Autocomplete<Apollo.DocumentStatus, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      inputValue={inputValue}
      options={[
        Apollo.DocumentStatus.Draft,
        Apollo.DocumentStatus.Signed,
        Apollo.DocumentStatus.Template,
        Apollo.DocumentStatus.Expired,
      ]}
      renderInput={params => (
        <TextField placeholder="Search statuses..." {...params} variant="standard" />
      )}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <DocumentStatusSimple status={option} />
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <FilterTags<Apollo.DocumentStatus>
            getTextFromTag={tag => capitalCase(tag)}
            tags={tags}
            truncateTags={truncateTags}
            onDelete={tag => handleValueChange(tags.filter(filterTag => tag !== filterTag))}
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={values}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, values) => handleValueChange(values)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
    />
  );
}
